import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api-client";

import type {
  NotificationStatus,
  NotificationStatusCounts,
  NotificationsResponse,
} from "@/types/notification";
import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";

type NotificationQueryData = NotificationsResponse | InfiniteData<NotificationsResponse>;

type PendingStatusUpdate = {
  sequence: number;
  status: NotificationStatus;
};

type NotificationUpdateState = {
  confirmedSequence: number;
  confirmedStatus: NotificationStatus;
  pending: PendingStatusUpdate[];
};

type OptimisticUpdateContext = {
  notificationId: string;
  sequence: number;
  status: NotificationStatus;
  previousStatus?: NotificationStatus;
  previousNotificationQueries: Array<[QueryKey, NotificationQueryData | undefined]>;
  previousCountQueries: Array<[QueryKey, number | undefined]>;
  previousCountsQueries: Array<[QueryKey, NotificationStatusCounts | undefined]>;
};

const notificationUpdates = new WeakMap<QueryClient, Map<string, NotificationUpdateState>>();
let updateSequence = 0;

export type UpdateNotificationStatusInput = {
  id: string;
  status: NotificationStatus;
};

export const updateNotificationStatus = async ({ id, status }: UpdateNotificationStatusInput) => {
  return api.put(`/carburate/notifications/messages/${id}/`, { status });
};

export const useUpdateNotificationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotificationStatus,
    onMutate: async ({ id, status }): Promise<OptimisticUpdateContext> => {
      await queryClient.cancelQueries({
        predicate: ({ queryKey }) =>
          ["notifications", "notifications-count", "notifications-counts"].includes(
            String(queryKey[0])
          ),
      });

      const previousNotificationQueries = queryClient.getQueriesData<NotificationQueryData>({
        queryKey: ["notifications"],
      });
      const previousCountQueries = queryClient.getQueriesData<number>({
        queryKey: ["notifications-count"],
      });
      const previousCountsQueries = queryClient.getQueriesData<NotificationStatusCounts>({
        queryKey: ["notifications-counts"],
      });
      const previousStatus = findNotificationStatus(previousNotificationQueries, id);
      const sequence = ++updateSequence;

      if (previousStatus) {
        const states = getNotificationUpdateStates(queryClient);
        const state = states.get(id) ?? {
          confirmedSequence: 0,
          confirmedStatus: previousStatus,
          pending: [],
        };

        state.pending.push({ sequence, status });
        states.set(id, state);

        updateNotificationCache(queryClient, id, previousStatus, getDesiredStatus(state));
      }

      return {
        notificationId: id,
        sequence,
        status,
        previousStatus,
        previousNotificationQueries,
        previousCountQueries,
        previousCountsQueries,
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousStatus) {
        finishOptimisticUpdate(queryClient, context, false);
      }
    },
    onSuccess: (_data, _variables, context) => {
      if (context?.previousStatus) {
        finishOptimisticUpdate(queryClient, context, true);
      }
    },
  });
};

function getNotificationUpdateStates(queryClient: QueryClient) {
  let states = notificationUpdates.get(queryClient);

  if (!states) {
    states = new Map();
    notificationUpdates.set(queryClient, states);
  }

  return states;
}

function findNotificationStatus(
  queries: Array<[QueryKey, NotificationQueryData | undefined]>,
  id: string
) {
  for (const [, data] of queries) {
    if (!data) continue;

    const pages = "pages" in data ? data.pages : [data];
    for (const page of pages) {
      const notification = page.results.find((item) => item.id === id);
      if (notification) return notification.status;
    }
  }

  return undefined;
}

function getDesiredStatus(state: NotificationUpdateState) {
  return state.pending.reduce(
    (latest, update) => (update.sequence > latest.sequence ? update : latest),
    { sequence: state.confirmedSequence, status: state.confirmedStatus }
  ).status;
}

function finishOptimisticUpdate(
  queryClient: QueryClient,
  context: OptimisticUpdateContext,
  succeeded: boolean
) {
  const states = getNotificationUpdateStates(queryClient);
  const state = states.get(context.notificationId);
  if (!state) return;

  state.pending = state.pending.filter((update) => update.sequence !== context.sequence);

  if (succeeded && context.sequence >= state.confirmedSequence) {
    state.confirmedSequence = context.sequence;
    state.confirmedStatus = context.status;
  }

  const currentStatus = findNotificationStatus(
    queryClient.getQueriesData<NotificationQueryData>({ queryKey: ["notifications"] }),
    context.notificationId
  );
  const desiredStatus = getDesiredStatus(state);

  if (currentStatus && currentStatus !== desiredStatus) {
    updateNotificationCache(queryClient, context.notificationId, currentStatus, desiredStatus);
  }

  if (state.pending.length === 0) {
    states.delete(context.notificationId);
  }
}

function updateNotificationCache(
  queryClient: QueryClient,
  id: string,
  previousStatus: NotificationStatus,
  status: NotificationStatus
) {
  for (const [queryKey, data] of queryClient.getQueriesData<NotificationQueryData>({
    queryKey: ["notifications"],
  })) {
    if (!data) continue;

    const queryStatus =
      queryKey[1] === "infinite"
        ? (queryKey[2] as { status?: NotificationStatus } | undefined)?.status
        : (queryKey[1] as { status?: NotificationStatus } | undefined)?.status;
    const updatePage = (page: NotificationsResponse) => {
      let changed = false;
      const results = page.results.flatMap((notification) => {
        if (notification.id !== id || notification.status === status) return [notification];

        changed = true;
        return queryStatus && queryStatus !== status ? [] : [{ ...notification, status }];
      });

      return changed
        ? {
            ...page,
            results,
            total: queryStatus && queryStatus !== status ? Math.max(0, page.total - 1) : page.total,
          }
        : page;
    };

    if ("pages" in data) {
      const pages = data.pages.map(updatePage);
      if (pages.some((page, index) => page !== data.pages[index])) {
        queryClient.setQueryData(queryKey, { ...data, pages });
      }
    } else {
      queryClient.setQueryData(queryKey, updatePage(data));
    }
  }

  if (previousStatus === status) return;

  queryClient.setQueriesData<NotificationStatusCounts>(
    { queryKey: ["notifications-counts"] },
    (counts) =>
      counts
        ? {
            ...counts,
            [previousStatus]: Math.max(0, counts[previousStatus] - 1),
            [status]: counts[status] + 1,
          }
        : counts
  );

  for (const [queryKey] of queryClient.getQueriesData<number>({
    queryKey: ["notifications-count"],
  })) {
    const filter = (queryKey[1] as { status?: NotificationStatus } | undefined)?.status;
    if (filter !== previousStatus && filter !== status) continue;

    queryClient.setQueryData<number>(queryKey, (count) =>
      count === undefined ? count : Math.max(0, count + (filter === previousStatus ? -1 : 1))
    );
  }
}
